import { addDays, format, parseISO } from "date-fns";

// Öffnungszeiten je Saison. Sommer: April bis September, Winter: Oktober bis März.
// Die Bestell-/Lieferzeiten prüft das Backend (scripts/delivery.js) selbst.
export const SEASONS = {
    winter: {
        name: "Winter",
        label: "Winter · Okt – März",
        title: "Winteröffnungszeiten (Oktober bis März)",
        rows: [
            { days: "Mo & Di", hours: ["Ruhetag"] },
            { days: "Mi – Fr", hours: ["11:30 – 14:00 Uhr", "17:00 – 23:00 Uhr"] },
            {
                days: "Samstag",
                hours: ["17:00 – 23:00 Uhr"],
                note: "Mittags auf Anfrage ab 20 Personen",
            },
            { days: "So & Feiertag", hours: ["11:30 – 22:00 Uhr durchgehend"] },
        ],
        // Kurzform je Wochentag (0 = Sonntag), null = Ruhetag
        byWeekday: [
            "11:30 – 22:00 Uhr · Küche bis 20:30",
            null,
            null,
            "11:30 – 14:00 · 17:00 – 23:00 Uhr",
            "11:30 – 14:00 · 17:00 – 23:00 Uhr",
            "11:30 – 14:00 · 17:00 – 23:00 Uhr",
            "17:00 – 23:00 Uhr · Küche bis 21:30",
        ],
        lunchDays: "Mi – Fr",
        lunchDaysLong: "Mittwoch bis Freitag",
        deliveryDays: "Mittwoch bis Samstag",
    },
    summer: {
        name: "Sommer",
        label: "Sommer · Apr – Sep",
        title: "Sommeröffnungszeiten (April bis September)",
        rows: [
            { days: "Montag", hours: ["Ruhetag"] },
            { days: "Di – Sa", hours: ["11:30 – 14:00 Uhr", "17:00 – 23:00 Uhr"] },
            { days: "So & Feiertag", hours: ["11:30 – 22:00 Uhr durchgehend"] },
        ],
        byWeekday: [
            "11:30 – 22:00 Uhr · Küche bis 20:30",
            null,
            "11:30 – 14:00 · 17:00 – 23:00 Uhr",
            "11:30 – 14:00 · 17:00 – 23:00 Uhr",
            "11:30 – 14:00 · 17:00 – 23:00 Uhr",
            "11:30 – 14:00 · 17:00 – 23:00 Uhr",
            "11:30 – 14:00 · 17:00 – 23:00 Uhr",
        ],
        lunchDays: "Di – Fr",
        lunchDaysLong: "Dienstag bis Freitag",
        deliveryDays: "Dienstag bis Samstag",
    },
};

export const KITCHEN_NOTE = "Warme Küche bis 21:30 Uhr, sonntags bis 20:30 Uhr.";

const WEEKDAYS = [
    "Sonntag",
    "Montag",
    "Dienstag",
    "Mittwoch",
    "Donnerstag",
    "Freitag",
    "Samstag",
];

export const getSeasonKey = (date = new Date()) => {
    const month = date.getMonth() + 1;
    return month >= 4 && month <= 9 ? "summer" : "winter";
};

export const getSeason = (date = new Date()) => SEASONS[getSeasonKey(date)];

// "YYYY-MM-DD" → "dd.MM."
export const formatClosureDate = (isoDate) => format(parseISO(isoDate), "dd.MM.");

// Texte für Hinweisleiste und Kontaktseite. closure: { startDate, endDate, note, active }.
// Nennt der Hinweistext das Datum der Wiedereröffnung schon, entfällt der eigene Satz dazu.
export const getClosureText = (closure) => {
    const reopenDate = format(addDays(parseISO(closure.endDate), 1), "dd.MM.");
    const noteHasReopen = Boolean(closure.note && closure.note.includes(reopenDate));
    return {
        label: closure.active ? "Vorübergehend geschlossen" : "Hinweis",
        period:
            closure.note ||
            `Vom ${formatClosureDate(closure.startDate)} bis ${formatClosureDate(closure.endDate)} geschlossen.`,
        reopen: noteHasReopen ? "" : `Ab ${reopenDate} sind wir wieder für Sie da.`,
        reopenDate,
    };
};

const nextOpenWeekday = (date) => {
    for (let offset = 1; offset <= 7; offset++) {
        const next = addDays(date, offset);
        if (getSeason(next).byWeekday[next.getDay()]) {
            return WEEKDAYS[next.getDay()];
        }
    }
    return null;
};

// Status für "Heute" oben rechts im Hero.
// closure / specialOpening: wie von /api/public/status/closure bzw. /specialOpening.
export const getTodayStatus = (date, { closure, specialOpening }) => {
    if (closure && closure.active) {
        return {
            label: "Vorübergehend geschlossen",
            hours: `Wieder geöffnet ab ${getClosureText(closure).reopenDate}`,
        };
    }

    if (specialOpening && specialOpening.active) {
        return {
            label: "Heute geöffnet",
            hours: specialOpening.note || "Sonderöffnung",
        };
    }

    const hours = getSeason(date).byWeekday[date.getDay()];
    if (!hours) {
        return {
            label: "Heute Ruhetag",
            hours: `Ab ${nextOpenWeekday(date)} wieder geöffnet`,
        };
    }
    return { label: "Heute geöffnet", hours };
};

// Tage ohne Reservierung: Ruhetage der Saison und Betriebsschließung.
export const isReservationDayClosed = (date, closure) => {
    if (closure) {
        const isoDate = format(date, "yyyy-MM-dd");
        if (isoDate >= closure.startDate && isoDate <= closure.endDate) {
            return true;
        }
    }
    return !getSeason(date).byWeekday[date.getDay()];
};

// Reservierbare Uhrzeiten für "YYYY-MM-DD", gleiche Regeln wie im Backend
// (backend/scripts/reservationSlots.js): halbstündlich, letzter Termin eine Stunde vor
// Küchenschluss, Samstagmittag nur auf Anfrage. Leeres Array = keine Reservierung.
const halfHours = (from, to) => {
    const toMinutes = (time) => {
        const [h, m] = time.split(":").map(Number);
        return h * 60 + m;
    };
    const slots = [];
    for (let t = toMinutes(from); t <= toMinutes(to); t += 30) {
        slots.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
    }
    return slots;
};
const LUNCH_SLOTS = halfHours("11:30", "13:30");
const EVENING_SLOTS = halfHours("17:00", "20:30");
const SUNDAY_SLOTS = halfHours("11:30", "19:30");
const RESERVATION_DAYS = {
    winter: { lunch: [3, 4, 5], evening: [3, 4, 5, 6] },
    summer: { lunch: [2, 3, 4, 5], evening: [2, 3, 4, 5, 6] },
};

export const getReservationTimes = (isoDate) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate || "")) return [];
    const date = parseISO(isoDate);
    const weekday = date.getDay();
    if (weekday === 0) return SUNDAY_SLOTS;
    const days = RESERVATION_DAYS[getSeasonKey(date)];
    return [
        ...(days.lunch.includes(weekday) ? LUNCH_SLOTS : []),
        ...(days.evening.includes(weekday) ? EVENING_SLOTS : []),
    ];
};
